import React, { useState, useEffect } from 'react';
import { createEmployee, updateEmployee, getEmployees, getCredentials, getEmployeeTypeDisplayName, getHospitals } from '../services/api.ts';
import Toast from './Toast.tsx';
import AdminOnly from './AdminOnly.tsx';
import ReadOnlyBanner from './ReadOnlyBanner.tsx';
import { useAuth } from '../contexts/AuthContext.tsx';

const EmployeeForm: React.FC = () => {
  // Pregnancy status is special-category health data. The API returns it to Admins only
  // (backend get-employees.ts), so the roster shows the column to Admins only — a read-only
  // user sees neither the values nor a column that would read "No" for everyone.
  const { isAdmin } = useAuth();
  const [formData, setFormData] = useState({
    employeeType: 'nurse' as 'nurse' | 'doctor' | 'nurse-student-junior' | 'nurse-student-senior',
    firstName: '',
    lastName: '',
    homeHospitalId: '',
    credentials: [] as string[],
    isFloatable: true,
    maxConcurrentNurseCoverageRatio: 4,
    maxConcurrentStudentCoverageRatio: 2,
    isCardiacAnesthesiologist: false,
    isPediatricAnesthesiologist: false,
    studentCompetency: '' as '' | 'new' | 'intermediate' | 'green',
    isPregnant: false
  });
  const [editingEmployeeId, setEditingEmployeeId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error' | 'warning' | 'info'; text: string } | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [employees, setEmployees] = useState<any[]>([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [credentialOptions, setCredentialOptions] = useState<string[]>([]);
  const [credentialsError, setCredentialsError] = useState<string | null>(null);
  const [hospitals, setHospitals] = useState<any[]>([]);
  const [lastNameFilter, setLastNameFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    loadEmployees();
    loadCredentials();
    loadHospitals();
  }, []);

  const loadHospitals = async () => {
    try {
      const response = await getHospitals();
      setHospitals(response.hospitals || []);
    } catch (error) {
      console.error('Failed to load hospitals:', error);
    }
  };

  const loadCredentials = async () => {
    try {
      setCredentialsError(null);
      const response = await getCredentials();
      setCredentialOptions(response.credentials || []);
    } catch (error: any) {
      console.error('Failed to load credentials:', error);
      setCredentialOptions([]);
      setCredentialsError('Failed to load credentials');
    }
  };

  const loadEmployees = async () => {
    setLoadingEmployees(true);
    try {
      const response = await getEmployees();
      setEmployees(response.employees || []);
    } catch (error) {
      console.error('Error loading employees:', error);
    } finally {
      setLoadingEmployees(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage(null);

    try {
      if (editingEmployeeId) {
        // Update existing employee
        await updateEmployee(editingEmployeeId, formData);
        setToast({ type: 'success', text: 'Employee updated successfully!' });
        setEditingEmployeeId(null);
      } else {
        // Create new employee
        await createEmployee(formData);
        setToast({ type: 'success', text: 'Employee created successfully!' });
      }

      setFormData({
        employeeType: 'nurse',
        firstName: '',
        lastName: '',
        homeHospitalId: '',
        credentials: [],
        isFloatable: true,
        maxConcurrentNurseCoverageRatio: 4,
        maxConcurrentStudentCoverageRatio: 2,
        isCardiacAnesthesiologist: false,
        isPediatricAnesthesiologist: false,
        studentCompetency: '',
        isPregnant: false
      });
      // Refresh the employees list
      loadEmployees();
    } catch (error: any) {
      setErrorMessage(error.response?.data?.error || `Failed to ${editingEmployeeId ? 'update' : 'create'} employee`);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleCredentialsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const credential = e.target.value;
    const isChecked = e.target.checked;
    
    setFormData({
      ...formData,
      credentials: isChecked 
        ? [...formData.credentials, credential]
        : formData.credentials.filter(c => c !== credential)
    });
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.checked
    });
  };

  const handleEdit = (employee: any) => {
    setEditingEmployeeId(employee.employeeId);
    setFormData({
      employeeType: employee.employeeType,
      firstName: employee.firstName,
      lastName: employee.lastName,
      homeHospitalId: employee.homeHospitalId || '',
      credentials: employee.credentials || [],
      isFloatable: employee.isFloatable !== false,
      maxConcurrentNurseCoverageRatio: employee.maxConcurrentNurseCoverageRatio || 4,
      maxConcurrentStudentCoverageRatio: employee.maxConcurrentStudentCoverageRatio || 2,
      isCardiacAnesthesiologist: employee.isCardiacAnesthesiologist || false,
      isPediatricAnesthesiologist: employee.isPediatricAnesthesiologist || false,
      studentCompetency: employee.studentCompetency || '',
      isPregnant: employee.isPregnant || false
    });
    setErrorMessage(null);
    // Scroll to top of form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setEditingEmployeeId(null);
    setFormData({
      employeeType: 'nurse',
      firstName: '',
      lastName: '',
      homeHospitalId: '',
      credentials: [],
      isFloatable: true,
      maxConcurrentNurseCoverageRatio: 4,
      maxConcurrentStudentCoverageRatio: 2,
      isCardiacAnesthesiologist: false,
      isPediatricAnesthesiologist: false,
      studentCompetency: '',
      isPregnant: false
    });
    setErrorMessage(null);
  };

  // Filter employees by last name
  const filteredEmployees = employees.filter(employee =>
    employee.lastName.toLowerCase().includes(lastNameFilter.toLowerCase())
  );

  // Calculate pagination
  const totalPages = Math.ceil(filteredEmployees.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedEmployees = filteredEmployees.slice(startIndex, endIndex);

  // Reset to page 1 when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [lastNameFilter]);

  // Credential options = the credential catalog PLUS every hospital (so all facilities are selectable),
  // plus anything already on this employee (so existing picks always show). Matches the Credentials tab.
  const credentialChoices = (() => {
    const s = new Set<string>();
    for (const c of credentialOptions) if (c && c.trim()) s.add(c);
    for (const h of hospitals) if (h && h.name && h.name.trim()) s.add(h.name);
    for (const c of formData.credentials) if (c) s.add(c);
    return [...s].sort((a, b) => a.localeCompare(b));
  })();

  return (
    <>
      {toast && (
        <Toast
          message={toast.text}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      <div className="card">
        <ReadOnlyBanner />
        <AdminOnly>
        <h2>{editingEmployeeId ? 'Edit Employee' : 'Create Employee'}</h2>
        {editingEmployeeId && (
          <div className="alert alert-info">
            Editing employee: {formData.firstName} {formData.lastName}
          </div>
        )}
        {errorMessage && (
          <div className="alert alert-error">
            {errorMessage}
          </div>
        )}
        {credentialsError && (
          <div className="alert alert-error">{credentialsError}</div>
        )}

        <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="employeeType">Employee Type</label>
          <select
            id="employeeType"
            name="employeeType"
            value={formData.employeeType}
            onChange={handleChange}
            required
          >
            <option value="nurse">{getEmployeeTypeDisplayName('nurse')}</option>
            <option value="doctor">{getEmployeeTypeDisplayName('doctor')}</option>
            <option value="nurse-student-junior">{getEmployeeTypeDisplayName('nurse-student-junior')}</option>
            <option value="nurse-student-senior">{getEmployeeTypeDisplayName('nurse-student-senior')}</option>
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="firstName">First Name</label>
          <input
            type="text"
            id="firstName"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            required
            placeholder="Enter first name"
          />
        </div>

        <div className="form-group">
          <label htmlFor="lastName">Last Name</label>
          <input
            type="text"
            id="lastName"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            required
            placeholder="Enter last name"
          />
        </div>

        <div className="form-group">
          <label htmlFor="homeHospitalId">Home Hospital (Optional)</label>
          <select
            id="homeHospitalId"
            name="homeHospitalId"
            value={formData.homeHospitalId}
            onChange={handleChange}
          >
            <option value="">-- Select Hospital --</option>
            {hospitals.map(hospital => (
              <option key={hospital.hospitalId} value={hospital.hospitalId}>
                {hospital.name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Credentials (Optional)</label>
          <div className="credentials-checkboxes">
            {credentialChoices.map(credential => (
              <label key={credential} className="checkbox-label">
                <input
                  type="checkbox"
                  value={credential}
                  checked={formData.credentials.includes(credential)}
                  onChange={handleCredentialsChange}
                />
                <span className="checkbox-text">{credential}</span>
              </label>
            ))}
            {credentialChoices.length === 0 && !credentialsError && (
              <div style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>No credentials available</div>
            )}
          </div>
        </div>

        <div className="form-group" style={{ borderTop: '1px solid var(--border, #e2e8f0)', paddingTop: '16px', marginTop: '4px' }}>
          <label style={{ fontWeight: 600 }}>Radiation Safety</label>
          <label className="checkbox-label">
            <input
              type="checkbox"
              name="isPregnant"
              checked={formData.isPregnant}
              onChange={handleCheckboxChange}
            />
            <span className="checkbox-text">
              {formData.employeeType === 'doctor'
                ? 'Pregnant — avoid solo (doctor-only) ionizing-radiation rooms'
                : 'Pregnant — never place in an ionizing-radiation room'}
            </span>
          </label>
          <small style={{ display: 'block', marginTop: '4px', color: 'var(--text-muted)' }}>
            {formData.employeeType === 'doctor' ? (
              <>
                When checked, this anesthesiologist is not assigned <strong>solo</strong> (doctor-only)
                to a room with ionizing radiation (interventional radiology, EP lab, cath lab, vascular,
                orthopedic C-arm / bone cement, urology fluoroscopy, ERCP, angio/embolization).
                Supervising CRNAs in such a room is fine — a floating supervisor isn&apos;t continuously exposed.
              </>
            ) : (
              <>
                When checked, this person (CRNA or student nurse) is never allocated to a room with
                ionizing radiation (interventional radiology, EP lab, cath lab, vascular, orthopedic
                C-arm / bone cement, urology fluoroscopy, ERCP, angio/embolization). Those rooms are
                staffed by other (non-pregnant) staff instead; they stay available for all other cases
                (robotic, laparoscopic, EGD/colonoscopy, CABG, ENT, etc.).
              </>
            )}
          </small>
        </div>

        {formData.employeeType === 'doctor' && (
          <>
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="isFloatable"
                  checked={formData.isFloatable}
                  onChange={handleCheckboxChange}
                />
                <span className="checkbox-text">Can Float (allocate to multiple ORs concurrently)</span>
              </label>
              <small style={{ display: 'block', marginTop: '4px', color: 'var(--text-muted)' }}>
                If unchecked, doctor can only be allocated to one OR at a time
              </small>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="isCardiacAnesthesiologist"
                  checked={formData.isCardiacAnesthesiologist}
                  onChange={handleCheckboxChange}
                />
                <span className="checkbox-text">Cardiac Anesthesiologist</span>
              </label>
              <small style={{ display: 'block', marginTop: '4px', color: 'var(--text-muted)' }}>
                Symphony staffs cardiac rooms (CABG, TAVR, aortic) only with cardiac anesthesiologists
              </small>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="isPediatricAnesthesiologist"
                  checked={formData.isPediatricAnesthesiologist}
                  onChange={handleCheckboxChange}
                />
                <span className="checkbox-text">Pediatric Anesthesiologist</span>
              </label>
              <small style={{ display: 'block', marginTop: '4px', color: 'var(--text-muted)' }}>
                Only pediatric anesthesiologists may supervise at a children's hospital
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="maxConcurrentNurseCoverageRatio">Max Concurrent CRNA Coverage</label>
              <input
                type="number"
                id="maxConcurrentNurseCoverageRatio"
                name="maxConcurrentNurseCoverageRatio"
                value={formData.maxConcurrentNurseCoverageRatio}
                onChange={handleChange}
                min="1"
                max="10"
                placeholder="Default: 4"
              />
              <small style={{ display: 'block', marginTop: '4px', color: 'var(--text-muted)' }}>
                Max number of ORs with CRNAs this doctor can cover concurrently (default: 4)
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="maxConcurrentStudentCoverageRatio">Max Concurrent Senior Student Coverage</label>
              <input
                type="number"
                id="maxConcurrentStudentCoverageRatio"
                name="maxConcurrentStudentCoverageRatio"
                value={formData.maxConcurrentStudentCoverageRatio}
                onChange={handleChange}
                min="1"
                max="10"
                placeholder="Default: 2"
              />
              <small style={{ display: 'block', marginTop: '4px', color: 'var(--text-muted)' }}>
                Max number of ORs with senior students this doctor can cover concurrently (default: 2)
              </small>
            </div>
          </>
        )}

        {(formData.employeeType === 'nurse-student-junior' || formData.employeeType === 'nurse-student-senior') && (
          <div className="form-group">
            <label htmlFor="studentCompetency">Student Competency</label>
            <select
              id="studentCompetency"
              name="studentCompetency"
              value={formData.studentCompetency}
              onChange={handleChange}
            >
              <option value="">Not set</option>
              <option value="new">New (red — cannot function independently)</option>
              <option value="intermediate">Intermediate (yellow — must pair with a CRNA or solo doc)</option>
              <option value="green">Green (can run solo at 1 doc : 2 students)</option>
            </select>
            <small style={{ display: 'block', marginTop: '4px', color: 'var(--text-muted)' }}>
              New/intermediate students must be paired with a CRNA or a solo doctor and don't change staffing ratios
            </small>
          </div>
        )}

        <div style={{ display: 'flex', gap: '10px' }}>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? (editingEmployeeId ? 'Updating...' : 'Creating...') : (editingEmployeeId ? 'Update Employee' : 'Create Employee')}
          </button>
          {editingEmployeeId && (
            <button type="button" className="btn btn-secondary" onClick={handleCancelEdit} disabled={loading}>
              Cancel
            </button>
          )}
        </div>
      </form>
        </AdminOnly>

      <div className="employees-table">
        <h3>Existing Employees</h3>

        <div className="form-group" style={{ marginBottom: '20px' }}>
          <label htmlFor="lastNameFilter">Filter by Last Name</label>
          <input
            type="text"
            id="lastNameFilter"
            value={lastNameFilter}
            onChange={(e) => setLastNameFilter(e.target.value)}
            placeholder="Type to filter by last name..."
            style={{ maxWidth: '400px' }}
          />
          {lastNameFilter && (
            <small style={{ display: 'block', marginTop: '4px', color: 'var(--text-muted)' }}>
              Showing {filteredEmployees.length} of {employees.length} employees
            </small>
          )}
        </div>

        {loadingEmployees ? (
          <p>Loading employees...</p>
        ) : filteredEmployees.length === 0 ? (
          <p>No employees found{lastNameFilter ? ` matching "${lastNameFilter}"` : ''}.</p>
        ) : (
          <>
            <table>
              <thead>
                <tr>
                  <th>Employee ID</th>
                  <th>Type</th>
                  <th>First Name</th>
                  <th>Last Name</th>
                  <th>Home Hospital</th>
                  <th>Credentials</th>
                  <th>Floatable</th>
                  <th>Max CRNA Coverage</th>
                  <th>Max Student Coverage</th>
                  {isAdmin && <th>Pregnant</th>}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedEmployees.map((employee) => (
                  <tr key={employee.employeeId}>
                    <td>{employee.employeeId}</td>
                    <td>{getEmployeeTypeDisplayName(employee.employeeType)}</td>
                    <td>{employee.firstName}</td>
                    <td>{employee.lastName}</td>
                    <td>{employee.homeHospitalId ? hospitals.find(h => h.hospitalId === employee.homeHospitalId)?.name || employee.homeHospitalId : 'N/A'}</td>
                    <td>{employee.credentials && employee.credentials.length > 0 ? employee.credentials.join(', ') : 'None'}</td>
                    <td>{employee.employeeType === 'doctor' ? (employee.isFloatable === false ? 'No' : 'Yes') : 'N/A'}</td>
                    <td>{employee.employeeType === 'doctor' ? (employee.maxConcurrentNurseCoverageRatio ?? 4) : 'N/A'}</td>
                    <td>{employee.employeeType === 'doctor' ? (employee.maxConcurrentStudentCoverageRatio ?? 2) : 'N/A'}</td>
                    {isAdmin && <td>{employee.isPregnant ? 'Yes' : 'No'}</td>}
                    <td>
                      <AdminOnly>
                        <button
                          className="btn btn-small"
                          onClick={() => handleEdit(employee)}
                          disabled={editingEmployeeId === employee.employeeId}
                        >
                          Edit
                        </button>
                      </AdminOnly>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', marginTop: '20px' }}>
                <button
                  className="btn btn-small"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                <span style={{ padding: '0 10px' }}>
                  Page {currentPage} of {totalPages} ({filteredEmployees.length} total)
                </span>
                <button
                  className="btn btn-small"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
      </div>
    </>
  );
};

export default EmployeeForm;
